// Pure parts of the Reports rows table (Stephanie 2026-10-16) — no React, no
// app imports, so the offline gate can run them.
import { csvCell } from "./csv";

export type ReportList =
  | "orders"
  | "sales_orders"
  | "estimates"
  | "booked"
  | "invoices"
  | "pos"
  | "presentations"
  | "opportunities"
  | "projects"
  | "touchpoints";

export interface ReportRow {
  id: string;
  href: string | null;
  number: number | null;
  projectName: string | null;
  clientId: string | null;
  clientName: string | null;
  repName: string | null;
  status: string | null;
  date: string | null;
  inHandsDate: string | null;
  amount: string | null;
  amount2: string | null;
  detail: string | null;
}

export type ColKey = "number" | "projectName" | "clientName" | "repName" | "status" | "date" | "inHandsDate" | "detail" | "amount" | "amount2";

export interface Col {
  key: ColKey;
  label: string;
  money?: boolean;
}

/** Which columns each list shows, and what they're called. */
export const LIST_COLUMNS: Record<ReportList, Col[]> = (() => {
  const order: Col[] = [
    { key: "number", label: "Order #" },
    { key: "projectName", label: "Project" },
    { key: "clientName", label: "Account" },
    { key: "repName", label: "Rep" },
    { key: "status", label: "Stage" },
    { key: "date", label: "Created" },
    { key: "inHandsDate", label: "In hands" },
    { key: "amount", label: "Order total", money: true },
  ];
  const project: Col[] = [
    { key: "number", label: "Project #" },
    { key: "projectName", label: "Project" },
    { key: "clientName", label: "Account" },
    { key: "repName", label: "Rep" },
    { key: "status", label: "Stage" },
    { key: "date", label: "Created" },
    { key: "inHandsDate", label: "In hands" },
    { key: "amount", label: "Budget", money: true },
  ];
  return {
    orders: order,
    sales_orders: order,
    estimates: order,
    booked: order,
    invoices: [
      { key: "number", label: "Invoice #" },
      { key: "projectName", label: "Project" },
      { key: "clientName", label: "Account" },
      { key: "repName", label: "Rep" },
      { key: "status", label: "Status" },
      { key: "date", label: "Invoice date" },
      { key: "detail", label: "Order / due" },
      { key: "amount", label: "Total", money: true },
      { key: "amount2", label: "Balance due", money: true },
    ],
    pos: [
      { key: "number", label: "PO #" },
      { key: "projectName", label: "Project" },
      { key: "clientName", label: "Account" },
      { key: "detail", label: "Supplier" },
      { key: "repName", label: "Rep" },
      { key: "status", label: "Stage" },
      { key: "date", label: "Created" },
      { key: "inHandsDate", label: "In hands" },
      { key: "amount", label: "PO cost", money: true },
    ],
    presentations: [
      { key: "number", label: "Presentation #" },
      { key: "projectName", label: "Project" },
      { key: "clientName", label: "Account" },
      { key: "repName", label: "Rep" },
      { key: "status", label: "Status" },
      { key: "date", label: "Created" },
      { key: "detail", label: "Sent / views" },
      { key: "inHandsDate", label: "In hands" },
    ],
    opportunities: project,
    projects: project,
    touchpoints: [
      { key: "date", label: "When" },
      { key: "status", label: "Type" },
      { key: "clientName", label: "Account" },
      { key: "projectName", label: "Project" },
      { key: "repName", label: "By" },
      { key: "detail", label: "Subject" },
    ],
  };
})();

export const NOUN: Record<ReportList, string> = {
  orders: "orders",
  sales_orders: "orders",
  estimates: "estimates",
  booked: "orders",
  invoices: "invoices",
  pos: "purchase orders",
  presentations: "presentations",
  opportunities: "opportunities",
  projects: "projects",
  touchpoints: "activities",
};

const STATUS_WORDS: Record<string, string> = {
  quote: "Estimate",
  committed: "Sales order",
  in_production: "In production",
  sales_order: "Sales order",
  sent_to_client: "Sent",
  changes_requested: "Changes requested",
};

/** Pure: a stored status in plain words. */
export function statusWords(s: string | null): string {
  if (!s) return "—";
  if (STATUS_WORDS[s]) return STATUS_WORDS[s];
  const t = s.replace(/_/g, " ");
  return t.charAt(0).toUpperCase() + t.slice(1);
}

/** Pure: MM/DD/YY (Stephanie's preferred short date). */
export function shortDate(iso: string | null): string {
  if (!iso) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  // Timestamps are shown in the viewer's own day; plain dates as written.
  if (iso.length > 10) {
    const d = new Date(iso);
    if (!Number.isNaN(d.getTime())) {
      return `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}/${String(d.getFullYear()).slice(2)}`;
    }
  }
  return `${m[2]}/${m[3]}/${(m[1] ?? "").slice(2)}`;
}

export function money(v: string | null): string {
  if (v === null) return "—";
  const n = Number(v);
  return Number.isNaN(n) ? v : n.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

export function cellText(r: ReportRow, c: Col): string {
  const v = r[c.key];
  if (c.money) return money(v as string | null);
  if (c.key === "date" || c.key === "inHandsDate") return shortDate(v as string | null);
  if (c.key === "status") return statusWords(v as string | null);
  if (c.key === "number") return v === null ? "—" : `#${String(v)}`;
  return v === null || v === "" ? "—" : String(v);
}

/** Pure: rows as CSV (money as plain numbers so a spreadsheet can add them). */
export function rowsToCsv(rows: readonly ReportRow[], cols: readonly Col[]): string {
  // csvCell also defuses spreadsheet formulas (security audit 2026-10-21, M5).
  const esc = (s: string) => csvCell(s);
  const head = cols.map((c) => esc(c.label)).join(",");
  const body = rows.map((r) =>
    cols
      .map((c) => {
        const v = r[c.key];
        if (c.money) return v === null ? "" : esc(String(v));
        if (c.key === "number") return v === null ? "" : esc(String(v));
        return esc(cellText(r, c) === "—" ? "" : cellText(r, c));
      })
      .join(","),
  );
  return [head, ...body].join("\n");
}

/** Pure: filter by search text across every shown column, then sort. */
export function filterAndSort(
  rows: readonly ReportRow[],
  cols: readonly Col[],
  search: string,
  sort: { key: ColKey; dir: "asc" | "desc" } | null,
): ReportRow[] {
  const q = search.trim().toLowerCase();
  const kept = q === "" ? [...rows] : rows.filter((r) => cols.some((c) => cellText(r, c).toLowerCase().includes(q)));
  if (!sort) return kept;
  const col = cols.find((c) => c.key === sort.key);
  return sortRows(
    kept,
    (r) => {
      const v = r[sort.key];
      if (v === null || v === undefined) return null;
      return col?.money || sort.key === "number" ? Number(v) : String(v);
    },
    sort.dir,
  );
}


// ── Sort any report column A→Z and back (Stephanie 2026-10-16) ─────────────
export type SortDir = "asc" | "desc";
export interface SortState<K extends string = string> {
  key: K;
  dir: SortDir;
}

/** Pure: clicking a header — a new column starts A→Z, the same one flips. */
export function nextSort<K extends string>(cur: SortState<K> | null, key: K): SortState<K> {
  return cur?.key === key ? { key, dir: cur.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" };
}

/** Pure: sort by a value (numbers as numbers, text ignoring case); blanks always last. */
export function sortRows<T>(rows: readonly T[], value: (r: T) => string | number | null | undefined, dir: SortDir): T[] {
  const norm = (r: T): string | number | null => {
    const v = value(r);
    if (v === null || v === undefined || v === "") return null;
    return typeof v === "number" ? v : v.toLowerCase();
  };
  return [...rows].sort((a, b) => {
    const x = norm(a);
    const y = norm(b);
    if (x === null && y === null) return 0;
    if (x === null) return 1;
    if (y === null) return -1;
    const c = typeof x === "number" && typeof y === "number" ? x - y : String(x).localeCompare(String(y), undefined, { numeric: true });
    return dir === "asc" ? c : -c;
  });
}
